import { useEffect, type ReactNode } from "react";
import { anmeldeUrsprung, aufAppHost } from "../lib/hosts";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/http";
import { useStore } from "../store/useStore";
import { Toaster } from "../components/ui";
import { Footer } from "./Footer";
import { TopNav } from "./TopNav";
import { Suspense } from "react";
import { AdminSkelett, KartenSkelett, SeitenSkelett, UebersichtSkelett } from "../components/SeitenSkelett";
/* Immer ueber die eine Tuer des Ladewerks, nie direkt aus den Einzeldateien —
   nur so laesst sich ein Bauteil ersetzen, ohne alle Aufrufstellen anzufassen.
   Mit diesem Import kommt auch ladewerk.css in den Bau; sie haengt an
   <Enthuellung> und war ohne Aufrufstelle bisher gar nicht Teil des Bundles. */
import { Enthuellung, useLadephase, type Welle } from "../components/ladewerk";
import { DemoChip } from "./DemoChip";

/**
 * Ruhiger Rahmen — der Fallback fuer alles, was KEIN Dashboard ist.
 *
 * Er zeichnet bewusst nichts: die Endstruktur von Adminbereich, Konto und
 * Rechtstexten ist voellig verschieden, und ohne bekannte Endstruktur ist eine
 * Silhouette geraten. Ein Loader ist hier ebenfalls verboten — er waere erst ab
 * zwei Sekunden erwarteter Wartezeit zulaessig, und ein Chunk ist deutlich
 * frueher da. Bleibt: die Flaeche freihalten und Hilfsmitteln sagen, dass hier
 * gerade etwas laeuft.
 */
function RuhigerRahmen() {
  return <div aria-busy="true" style={{ minHeight: "60vh" }} />;
}

/**
 * Passenden Fallback zum Pfad waehlen.
 *
 * Vorher stand fuer JEDE Route unter der Shell das Dashboard-Drahtgitter — auch
 * vor /admin, /account und /legal. Dort erschien erst eine Projektschiene mit
 * Kennzahlkacheln und Diagramm und danach der Adminbereich mit elf Karten. Das
 * ist kein Layoutsprung, das ist ein Themenwechsel: man sieht kurz eine ganz
 * andere Seite entstehen als die, die kommt.
 *
 * TODO: Sobald die Skelett-Registry steht (Paket „Silhouetten", geplant als
 * components/SeitenSkelett-Registry: Pfadmuster -> Silhouette), den Fallback
 * dort NACHSCHLAGEN statt ihn hier nach Pfad zu raten. Diese Funktion ist der
 * eine Ort, der sich dafuer aendern muss.
 */
function fallbackFuer(pathname: string) {
  const dashboardartig = pathname === "/" || pathname.startsWith("/project") || pathname.startsWith("/overview");
  if (dashboardartig) return <SeitenSkelett />;
  /* Die Verwaltung ist die berechenbarste Seite des Hauses: Titelblock, dann
     eine feste Reihe zugeklappter Karten. Sie lief trotzdem auf den ruhigen
     Rahmen — gemessen 17 % Deckung, und die Fusszeile stand dabei sichtbar
     mitten im Bild, um beim Umschalten 300 px nach unten zu springen. Eine
     Liste Rechtecke kostet nichts und nimmt beides weg. */
  if (pathname.startsWith("/admin")) return <AdminSkelett />;
  return <RuhigerRahmen />;
}

/**
 * Der Fallback einer Suspense-Grenze, durch das Ladewerk gefuehrt.
 *
 * Warum ein eigenes Bauteil und kein nacktes <Enthuellung laedt>: der
 * Torwaechter ist die einzige Uhr des Ladewerks, und `laedt` roh
 * durchzureichen ist genau der Fehler, vor dem components/ladewerk/index.ts
 * warnt. Hier ist `laedt` konstant true, und das ist kein Umgehen, sondern die
 * Wahrheit: ein Fallback steht per Definition NUR waehrend des Ladens im Baum,
 * „fertig" gibt es an dieser Stelle nicht — React nimmt den Knoten weg.
 * `hatInhalt: false` ist ebenso ehrlich: beim Routenwechsel ist der alte
 * Bildschirm schon fort, es gibt nichts zu halten. Also Silhouette sofort,
 * ohne Verzoegerungstor. Weil die Enthuellung damit BEREITS im Ladezustand
 * einhaengt, steht die Textur vom ersten Bild an — kein Loch, kein Aufblitzen.
 *
 * GRENZE, damit sie niemand fuer ein Versehen haelt: die Kreuzblende ZURUECK
 * in den Inhalt findet hier nicht statt. Dafuer muesste die Enthuellung
 * ausserhalb der Grenze stehen und erfahren, WANN der Chunk da ist — und
 * Suspense gibt dieses Signal nicht heraus. Ein Melder im Fallback koennte es
 * nachbauen, aber Torwaechter und Enthuellung fassen ihre Zustaende in
 * passiven Effekten: die Enthuellung wuerde erst zwei Bilder nach dem Beginn
 * des Haltens von an=true auf an=false umschalten und dazwischen leer malen.
 * Ein sichtbares Loch ist schlechter als eine fehlende Ausblende.
 * Wo das Signal existiert, steht die Enthuellung deshalb aussen — siehe den
 * Diagrammblock in ProjectStats, der mit `diagrammeBereit` genau diesen Riegel
 * hat und darum die volle Blende bekommt.
 */
function Ladehuelle({ silhouette, welle = 1 }: { silhouette: ReactNode; welle?: Welle }) {
  const lade = useLadephase({ laedt: true, hatInhalt: false });
  return <Enthuellung laedt={lade.phase !== "ruhe"} silhouette={silhouette} welle={welle} />;
}

/**
 * Rueckweg aus einem entzogenen Projekt.
 *
 * Seit "/" schon VOR der Projektliste auf das zuletzt genutzte Projekt springt
 * (App.tsx, HomeRedirect), kann die Adresse auf einem Projekt landen, das
 * dieser Kennung gar nicht mehr gehoert — der Admin hat die Zuordnung entzogen,
 * das Projekt wurde geloescht, oder am selben Rechner meldet sich jemand
 * anderes an. ProjectStats zeigt dann „Projekt nicht gefunden": richtig, aber
 * eine Sackgasse ohne Ausgang. Denselben Zustand erreicht auch jeder geteilte
 * Link auf ein fremdes Projekt — der Rueckweg fehlte dort schon vorher.
 *
 * Geurteilt wird ERST, wenn die Liste da ist; ohne Liste bleibt alles stehen.
 * Korrigiert wird auf eine konkrete Adresse (erstes zugaengliches Projekt),
 * nie zurueck auf "/" — sonst schickte der Vorgriff dort sofort wieder auf
 * dasselbe entzogene Projekt und die Seite liefe im Kreis. Den Store raeumt die
 * TopNav im selben Durchgang auf (setLastProject auf das erste zugaengliche
 * Projekt), sie haengt an derselben Abfrage.
 *
 * Die Abfrage kostet nichts extra: die TopNav dieser Shell haelt denselben
 * Schluessel, react-query bedient beide aus einem Aufruf.
 */
function useEntzogenesProjekt(): string | null {
  const auth = useStore((s) => s.auth);
  const { pathname } = useLocation();
  const projectsQ = useQuery({ queryKey: ["projects"], queryFn: () => api.projects.list(), enabled: !!auth });
  const praefix = "/project/";
  const id = pathname.startsWith(praefix) ? (pathname.slice(praefix.length).split("/")[0] ?? "") : "";
  const liste = projectsQ.data;
  if (!id || !liste) return null;
  if (liste.some((p) => p.id === id)) return null;
  // Ohne jedes zugaengliche Projekt bleibt die Uebersicht — dort steht der
  // ehrliche Leerzustand („Admins legen Projekte an").
  const ersatz = liste[0];
  return ersatz ? `${praefix}${ersatz.id}` : "/overview";
}

/** Auth guard: redirects to /login when the route needs a session. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useStore((s) => s.auth);
  const authGeprueft = useStore((s) => s.authGeprueft);
  if (!auth) {
    // Auf app.* wohnt keine Anmeldeseite mehr — dorthin, wo sie wohnt.
    // Vorher den Boot-Abgleich abwarten: das Domain-Cookie kann die Sitzung
    // tragen, obwohl der Origin-eigene localStorage noch leer ist.
    if (aufAppHost()) {
      if (!authGeprueft) return <SeitenSkelett />;
      location.href = `${anmeldeUrsprung()}/login`;
      return null;
    }
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

/** Admin guard: bounces non-admins to the dashboard (mirrors prototype). */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const auth = useStore((s) => s.auth);
  const authGeprueft = useStore((s) => s.authGeprueft);
  if (!auth) {
    if (aufAppHost()) {
      if (!authGeprueft) return <SeitenSkelett />;
      location.href = `${anmeldeUrsprung()}/login`;
      return null;
    }
    return <Navigate to="/login" replace />;
  }
  if (auth.role !== "admin") return <Navigate to="/" replace />;
  return <>{children}</>;
}

/** Standard scrollable page layout: fixed TopNav + scroll area + footer. */
export function Shell() {
  const { pathname } = useLocation();
  const umleitung = useEntzogenesProjekt();

  // scroll to top on navigation (prototype navigate() behaviour)
  useEffect(() => {
    document.getElementById("main-scroll")?.scrollTo({ top: 0 });
    window.scrollTo({ top: 0 });
  }, [pathname]);

  return (
    <>
      <TopNav />
      <DemoChip />
      <main id="main-scroll" className="scroll" style={{ position: "fixed", inset: 0, top: "calc(var(--kopf-h) + var(--demo-band, 0px))", overflowY: "auto" }}>
        {/* Suspense HIER statt um die Routen: die Kopfleiste steht sofort,
            nur der Inhalt darunter skelettiert — und zwar mit der Silhouette,
            die zum Pfad passt (s. fallbackFuer). Der Fallback laeuft ueber das
            Ladewerk (Ladehuelle), damit auch der Routenwechsel unter derselben
            Uhr und denselben aria-Regeln steht: welle 1, denn was hier
            erscheint, IST die Grundstruktur. */}
        <Suspense fallback={<Ladehuelle silhouette={fallbackFuer(pathname)} welle={1} />}>
          {/* Die Umleitung ersetzt nur den INHALT: Kopfleiste und Fusszeile
              bleiben stehen, damit der eine Bildaufbau dazwischen nichts
              wegblitzen laesst. */}
          {umleitung ? <Navigate to={umleitung} replace /> : <Outlet />}
        </Suspense>
        <Footer />
      </main>
      <Toaster />
    </>
  );
}

/** Full-viewport layout for the Karte screen (no scroll, no footer). */
export function MapShell() {
  const { pathname } = useLocation();
  /* MapShell traegt ZWEI Bildschirme: die Projektkarte und die
     Uebersichtskarte. Beide bekamen bisher dasselbe KartenSkelett — auf
     /uebersicht versprach es damit ein Ebenenpanel, zwei Werkzeugsaeulen und
     einen Ebenenknopf, die es dort gar nicht gibt (gemessen 15 % Deckung, die
     schlechteste der App). Die Uebersicht hat ihre eigene, sehr viel
     karger moeblierte Silhouette. */
  const silhouette = pathname.startsWith("/uebersicht") ? <UebersichtSkelett /> : <KartenSkelett />;
  return (
    <>
      <TopNav />
      <DemoChip />
      <div style={{ position: "fixed", inset: 0, top: "calc(var(--kopf-h) + var(--demo-band, 0px))" }}>
        {/* welle 3: eine Karte ist Bildmaterial, sie kommt nach Struktur und
            Zahlen. KartenSkelett liegt absolut im festen Rahmen darueber — die
            Rasterhuelle der Enthuellung ist statisch positioniert und
            veraendert den Bezugsrahmen dieser Absolutwerte nicht. */}
        <Suspense fallback={<Ladehuelle silhouette={silhouette} welle={3} />}>
          <Outlet />
        </Suspense>
      </div>
      <Toaster />
    </>
  );
}
